import { computed, signal, Signal } from '@preact/signals';
import {
  isFooterAdUnit,
  isHeaderAdUnit,
  isInterstitialAdUnit,
  isOptinVideoAdUnit
} from '../adUnit';
import { isIos } from '../environment-utils';
import { ConfigStore } from './config.store';
import { reaction } from '../utils/preact-utils';
import { getClosestRatio, getRatioFromSizes } from '../ratio';
import { AdContainerStore } from './ad-container.store';
import { IVideoSizes } from '../video-utils';

import { hasSkin } from '../../../formats/video-sdk3/lib/utils';
import {
  CachedMediaFile,
  MediaFile
} from '../../../formats/video-sdk3/lib/vast/media-file';
import {
  trackProgress,
  trackRealVideoProgress
} from '../../../formats/video-sdk3/lib/video-progress-service';
import VASTClient from '../../../formats/video-sdk3/lib/vast/vast-client';

interface IWindowSize {
  width: number;
  height: number;
}

type VideoState = 'paused' | 'playing' | 'loading';

export class VideoStore {
  mediaFile: Signal<CachedMediaFile | MediaFile | null>;
  configStore: ConfigStore;
  videoPlayer: Signal<HTMLVideoElement | undefined>;
  shouldPlayVideo: Signal<boolean>;
  hasVideoStarted: Signal<boolean>;
  isVideoCompleted: Signal<boolean>;
  isVideoExpanded: Signal<boolean>;
  shouldDisplayBlur: Signal<boolean>;
  windowSize: Signal<IWindowSize>;
  shouldAddVideoBackground: Signal<boolean>;
  _vast: Signal<VASTClient | null>;

  _isVpaid: Signal<boolean>;
  _videoSizes: Signal<IVideoSizes>;
  videoRealRatio: Signal<number>;
  videoClosestRatio: Signal<number>;
  videoState!: Signal<VideoState>;
  videoProgress: Signal<number>;

  adContext: AdContext;

  constructor(
    config: ConfigStore,
    context: AdContext,
    adContainerStore: AdContainerStore
  ) {
    this.configStore = config;

    this.adContext = context;

    this.mediaFile = signal<CachedMediaFile | MediaFile | null>(null);
    this.videoPlayer = signal<HTMLVideoElement | undefined>(undefined);

    this.hasVideoStarted = signal(false);
    this.isVideoCompleted = signal(false);

    this.videoProgress = signal(0);

    this.shouldPlayVideo = computed(() => {
      return adContainerStore.isViewable.value && !!this.videoPlayer.value;
    });

    this.isVideoExpanded = computed(() => {
      return (
        this._isInterstitialAdUnit() ||
        adContainerStore.state.value === 'expanded'
      );
    });

    this._vast = signal(null);
    this._isVpaid = signal(false);

    this.windowSize = signal({
      width: window.innerWidth,
      height: window.innerHeight
    });

    this._videoSizes = signal<IVideoSizes>({
      vast_video_width: undefined,
      vast_video_height: undefined,
      video_width: undefined,
      video_height: undefined
    });

    this.shouldAddVideoBackground = signal(
      this._shouldInitiallyAddVideoBackground()
    );

    this.shouldDisplayBlur = computed(() => {
      return !!(
        this._shouldDisplayBlur() &&
        this.configStore.config.video?.isBlurBackground
      );
    });

    this.videoRealRatio = computed(() => {
      const videoSizes = this._videoSizes.value;
      const creativeRatio =
        this.configStore.config.video &&
        this.configStore.config.video.creativeRatio;
      return getRatioFromSizes(creativeRatio, videoSizes);
    });

    this.videoClosestRatio = computed(() => {
      const videoRealRatio = this.videoRealRatio.value;
      return getClosestRatio(videoRealRatio);
    });

    this._setupObservers();
  }

  set vast(vast: VASTClient | null) {
    this._vast.value = vast;
  }

  get vast(): VASTClient | null {
    return this._vast.value;
  }

  get isVpaid(): boolean {
    return this._isVpaid.value;
  }

  set isVpaid(isVpaid: boolean) {
    this._isVpaid.value = isVpaid;
  }

  get videoSizes(): IVideoSizes {
    return this._videoSizes.value;
  }

  setMediaFile(mediaFile: CachedMediaFile | MediaFile): void {
    this.mediaFile.value = mediaFile;
  }

  // TODO: move this into a AdContextStore
  get isChildPrivacyApplied(): boolean {
    return Boolean(this.adContext.childPrivacyApplied);
  }

  setVideoPlayer(videoPlayer: HTMLVideoElement): void {
    this.videoPlayer.value = videoPlayer;
  }

  setVideoStarted(): void {
    this.hasVideoStarted.value = true;
  }

  refreshVideoSizes(): void {
    this._videoSizes.value = this._buildVideoSizes(
      this.mediaFile.value,
      this.videoPlayer.value || null
    );
  }

  setVideoCompleted(): void {
    this.isVideoCompleted.value = true;
  }

  updateWindowSize(windowSize: IWindowSize): void {
    this.windowSize.value = windowSize;
  }

  shouldDisplaySkin(): boolean {
    return (
      hasSkin(this.configStore.config) &&
      (this.configStore.config.skin?.visibleByDefault ||
        this.isVideoExpanded.value)
    );
  }

  shouldRenderTransparentVideo(): boolean {
    return !!(
      this.configStore.config.video?.enableOpacityOnShow &&
      !this.hasVideoStarted.value
    );
  }

  onVideoProgress(progress: number): boolean {
    if (!this.vast) {
      return false;
    }

    this.videoProgress.value = progress;

    // Track progress and check if completion should be triggered
    // Returns true if progress >= billedDuration (video should complete)
    const shouldComplete = trackProgress(progress, this.vast);

    // Track full video duration progress for "real" quartile stats
    const { enableRealQuartiles } = this.configStore.config;
    trackRealVideoProgress(
      progress,
      this.vast.getDuration(),
      Boolean(enableRealQuartiles)
    );

    return shouldComplete;
  }

  _shouldDisplayBlur(): boolean {
    if (
      !this.videoPlayer.value ||
      this.configStore.config.video?.isBlurForcedDisabled ||
      this.shouldDisplaySkin()
    ) {
      return false;
    }

    return true;
  }

  // TODO: to be moved into header/footer config + featureEnabling file
  _shouldInitiallyAddVideoBackground(): boolean {
    return (
      (isHeaderAdUnit(this.adContext.adUnitType) ||
        isFooterAdUnit(this.adContext.adUnitType)) &&
      isIos()
    );
  }

  _isInterstitialAdUnit(): boolean {
    return (
      isInterstitialAdUnit(this.adContext.adUnitType) ||
      isOptinVideoAdUnit(this.adContext.adUnitType)
    );
  }

  // Moved from legacy/formats/video-sdk3/components/index.js
  _iosFixHeaderAdDelay(): void {
    // fix for iOS 15 header ad : video appeared couple of ms after the container. adding background fix the issue
    // remove rgba(0,0,0,.1) video background
    if (this.shouldAddVideoBackground.value) {
      setTimeout(() => {
        this.shouldAddVideoBackground.value = false;
      }, 2000);
    }
  }

  _setupObservers(): void {
    const destroyVideoSizesReaction = reaction(
      () => [
        this.mediaFile.value,
        this.videoPlayer.value,
        this.hasVideoStarted.value
      ],
      ([mediaFile, videoPlayer, hasStarted]: [
        CachedMediaFile | MediaFile | null,
        HTMLVideoElement | null,
        boolean
      ]) => {
        const videoSizes = this._buildVideoSizes(mediaFile, videoPlayer);

        if (
          hasStarted ||
          Object.keys(videoSizes).every(
            k => videoSizes[k as keyof IVideoSizes] !== undefined
          )
        ) {
          destroyVideoSizesReaction && destroyVideoSizesReaction();
        }

        this._videoSizes.value = videoSizes;
      }
    );
  }

  _buildVideoSizes(
    mediaFile: CachedMediaFile | MediaFile | null,
    videoPlayer: HTMLVideoElement | null
  ): IVideoSizes {
    const videoSizes: IVideoSizes = {
      vast_video_width: undefined,
      vast_video_height: undefined,
      video_width: undefined,
      video_height: undefined
    };

    if (mediaFile && 'width' in mediaFile) {
      videoSizes.vast_video_width = mediaFile.width || undefined;
      videoSizes.vast_video_height = mediaFile.height || undefined;
    }

    if (videoPlayer) {
      videoSizes.video_width = videoPlayer.videoWidth || undefined;
      videoSizes.video_height = videoPlayer.videoHeight || undefined;
    }

    return videoSizes;
  }
}
